import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";

import { useCurrentUser, useLogout } from "../lib/currentUser";
import { requireAuth } from "../lib/requireAuth";
import { RoleBadge } from "../components/RoleBadge";

export const Route = createFileRoute("/me")({
  beforeLoad: ({ context, location }) =>
    requireAuth(context.queryClient, location.href),
  component: ProfilePage,
});

/**
 * Minimal profile page — Phase 1G ships the view, not a settings
 * surface. Future iterations add language preference / timezone /
 * deadline override / etc. once we have a reason to want them.
 *
 * Routes the user to `/login` after sign-out so the next visit
 * doesn't silently land them back on the (fallback-as-Jorge)
 * home.
 */
function ProfilePage() {
  const { data: user, isLoading } = useCurrentUser();
  const navigate = useNavigate();
  const logoutM = useLogout();

  if (isLoading) {
    return (
      <section className="mx-auto max-w-2xl px-4 sm:px-6 py-16 text-ink-400">
        Loading…
      </section>
    );
  }
  if (!user) {
    return (
      <section className="mx-auto max-w-2xl px-4 sm:px-6 py-16 text-center">
        <p className="text-ink-500 mb-6">Not signed in.</p>
        <Link
          to="/login"
          className="inline-block bg-accent px-5 py-3 text-accent-fg hover:bg-accent-hover transition-colors"
        >
          Sign in
        </Link>
      </section>
    );
  }

  const signOut = async () => {
    try {
      await logoutM.mutateAsync();
    } catch {
      /* swallow — UI just routes regardless */
    }
    navigate({ to: "/login" });
  };

  const initial = (user.name || user.email).charAt(0).toUpperCase();

  return (
    <section className="mx-auto max-w-xl px-4 sm:px-6 py-12 sm:py-16">
      <div className="eyebrow mb-3">Profile</div>
      <h1 className="font-serif text-3xl sm:text-4xl text-ink-900 mb-3 tracking-tight">
        {user.name}
      </h1>
      <div className="hairline mb-6 sm:mb-8" />

      <div className="flex items-start gap-4 mb-8">
        <div className="h-16 w-16 rounded-full bg-ink-100 text-ink-700 inline-flex items-center justify-center font-serif font-medium text-2xl shrink-0 select-none">
          {user.pictureUrl ? (
            <img
              src={user.pictureUrl}
              alt=""
              className="h-16 w-16 rounded-full object-cover"
            />
          ) : (
            initial
          )}
        </div>
        <dl className="flex-1 text-sm space-y-3">
          <div>
            <dt className="eyebrow">Email</dt>
            <dd className="text-ink-900 break-all">{user.email}</dd>
          </div>
          <div>
            <dt className="eyebrow">Role</dt>
            <dd>
              <RoleBadge role={user.role} size="md" />
            </dd>
          </div>
          <div>
            <dt className="eyebrow">User ID</dt>
            <dd className="text-ink-500 font-mono text-xs tabular-nums">
              {user.id}
            </dd>
          </div>
        </dl>
      </div>

      <div className="border-t border-ink-100 pt-6 flex flex-wrap gap-3">
        <Link
          to="/login"
          className="border border-ink-200 px-4 py-2 text-ink-700 hover:border-ink-700 hover:text-ink-900 transition-colors"
        >
          Switch user
        </Link>
        <button
          type="button"
          onClick={signOut}
          disabled={logoutM.isPending}
          className="bg-accent px-4 py-2 text-accent-fg hover:bg-accent-hover transition-colors disabled:opacity-50"
        >
          {logoutM.isPending ? "Signing out…" : "Sign out"}
        </button>
      </div>
    </section>
  );
}
